import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { formatThaiShortDateTwoDigitYear, getAgeOnDate } from 'common/utils/date'
import { fInteger, fNumber } from 'common/utils/formatNumber'
import { labelWithSpecies } from 'common/utils/species'
import { useFarms } from 'modules/farm/api/queries'
import { useGetCycleById } from 'modules/pond/api/queries'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import ChildPondChip from 'modules/pond/components/ChildPondChip'
import MoreAction from 'modules/pond/components/MoreAction'
import { useOpenPondCycle } from 'modules/pond/components/PondPageWrapper/useOpenPondCycle'
import { SpeciesName } from 'modules/pond/constants'
import { PartialTransfer } from 'modules/shrimpTransfer/api/types'
import SpeciesIcon from 'modules/shrimpTransfer/components/SpeciesIcon'
import {
  estimateTransfer,
  isCrossFarmTransfer,
} from 'modules/shrimpTransfer/utils/transferEstimate'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CallMadeIcon from '@mui/icons-material/CallMade'
import CallReceivedIcon from '@mui/icons-material/CallReceived'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined'
import ScheduleIcon from '@mui/icons-material/Schedule'
import StraightenIcon from '@mui/icons-material/Straighten'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'

interface ShrimpTransferViewDialogProps {
  open: boolean
  onClose: () => void
  data: PartialTransfer
  onDelete?: () => void
}

interface PondBoxProps {
  label: string
  pondName?: string
  farmName?: string
  highlighted?: boolean
  showChildChip?: boolean
  onOpen?: () => void
  openLabel?: string
}

const PondBox = ({
  label,
  pondName,
  farmName,
  highlighted,
  showChildChip,
  onOpen,
  openLabel,
}: PondBoxProps) => {
  const { t } = useTranslation('shrimpTransfer')
  const clickable = !!onOpen
  return (
    <Box
      component={clickable ? ButtonBase : 'div'}
      {...(clickable ? { onClick: onOpen, 'aria-label': openLabel } : {})}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 2,
        border: 2,
        borderColor: highlighted ? 'primary.main' : 'transparent',
        bgcolor: highlighted ? 'primary.lighter' : 'grey.100',
        ...(clickable && {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
          '&:hover': { bgcolor: 'primary.light' },
        }),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color={highlighted ? 'primary.dark' : 'text.secondary'}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" color={highlighted ? 'primary.dark' : 'text.primary'}>
            {t('transfer.pondLabel', { name: pondName ?? '' })}
          </Typography>
          {showChildChip && <ChildPondChip />}
        </Stack>
        {farmName && (
          <Typography variant="body2" color={highlighted ? 'primary.dark' : 'text.secondary'}>
            {t('transfer.farmLabel', { name: farmName })}
          </Typography>
        )}
      </Box>
      {clickable && <ChevronRightIcon sx={{ color: 'primary.dark', flexShrink: 0 }} />}
    </Box>
  )
}

interface InfoRowProps {
  icon: ReactNode
  label: string
  value: string
  unit?: string
}

const InfoRow = ({ icon, label, value, unit }: InfoRowProps) => (
  <>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ py: 1.75 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
        {icon}
        <Typography color="text.secondary">{label}</Typography>
      </Stack>
      <Typography fontWeight={700} textAlign="right">
        {value}
        {unit ? ` ${unit}` : ''}
      </Typography>
    </Stack>
    <Divider />
  </>
)

const ShrimpTransferViewDialog = ({
  open,
  onClose,
  data,
  onDelete,
}: ShrimpTransferViewDialogProps) => {
  const { t } = useTranslation('shrimpTransfer')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const { data: cycleInfo } = useGetCycleById()
  // The API's `age` is the cycle's age today, not on the transfer date, so
  // derive it from the cycle start (DOF-1348); fall back to the API value.
  const ageOnTransfer = getAgeOnDate(cycleInfo?.startDate, data?.transfer_date) ?? data?.age
  const { farmId: routeFarmId } = useParams()
  const { data: farmData } = useFarms()
  const openPondCycle = useOpenPondCycle()
  const destinationFarmId = data?.destination_farm_id
  // The farm list is scope-filtered by the backend, so presence means openable.
  const canOpenDestination =
    !!data?.destination_is_child_pond &&
    !!destinationFarmId &&
    !!data.destination_pond_id &&
    (destinationFarmId === routeFarmId ||
      !!farmData?.farms.some((farm) => farm.id === destinationFarmId))
  const handleOpenDestination = () => {
    if (!data || !destinationFarmId) return
    onClose()
    openPondCycle({
      farmId: destinationFarmId,
      pondId: data.destination_pond_id,
      cycleId: data.destination_cycle_id,
    })
  }
  const isOut = data?.type === 'out'
  const crossFarm = !!data && isCrossFarmTransfer(data)
  const estimate = data
    ? estimateTransfer({
        isFish,
        weightKg: data.amount,
        size: data.amount_per_kg,
        cost: data.cost_per_kg,
      })
    : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, p: 0 } }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={700}>
            {labelWithSpecies(t('transfer.viewTitle'), species)}
          </Typography>
          <Stack direction="row" alignItems="center">
            {isOut && (
              <MoreAction
                featureKey={FeatureKey.ShrimpTransfer}
                onClickOptions={[
                  {
                    icon: <DeleteOutlineIcon color="error" />,
                    text: t('transfer.deleteItem'),
                    action: data?.id ? () => onDelete?.() : undefined,
                    crudIdx: CRUDAction.Delete,
                  },
                ]}
              />
            )}
            <IconButton aria-label="close" onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box
            sx={{
              width: (theme) => theme.spacing(5.5),
              height: (theme) => theme.spacing(5.5),
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isOut ? 'error.lighter' : 'success.lighter',
              color: isOut ? 'error.main' : 'success.main',
            }}
          >
            {isOut ? <CallMadeIcon /> : <CallReceivedIcon />}
          </Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700}>
              {isOut ? t('transfer.moveOut') : t('transfer.moveIn')}
            </Typography>
            {crossFarm && (
              <Chip
                size="small"
                label={t('transfer.crossFarm')}
                sx={{ bgcolor: 'info.lighter', color: 'info.dark', fontWeight: 600 }}
              />
            )}
          </Stack>
          <Typography fontWeight={700} color="text.secondary" textAlign="right">
            {formatThaiShortDateTwoDigitYear(data?.transfer_date || '', { showTime: true })}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <PondBox
            label={t('transfer.from')}
            pondName={data?.source_pond_name}
            farmName={data?.source_farm_name}
          />
          <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
          <PondBox
            highlighted
            label={t('transfer.toPond')}
            pondName={data?.destination_pond_name}
            farmName={data?.destination_farm_name}
            showChildChip={data?.destination_is_child_pond}
            onOpen={canOpenDestination ? handleOpenDestination : undefined}
            openLabel={t('transfer.openPond', { name: data?.destination_pond_name ?? '' })}
          />
        </Stack>

        <Divider />
        {!!ageOnTransfer && (
          <InfoRow
            icon={<ScheduleIcon fontSize="small" />}
            label={t('transfer.ageOnTransfer', { species })}
            value={String(ageOnTransfer)}
            unit={t('transfer.dateUnit')}
          />
        )}
        <InfoRow
          icon={<SpeciesIcon isFish={isFish} />}
          label={t('transfer.quantity')}
          value={fNumber(data?.amount ?? 0, { disabledDecimal: true })}
          unit={t('transfer.kg')}
        />
        <InfoRow
          icon={<StraightenIcon fontSize="small" />}
          label={t('transfer.shrimpSize', { species })}
          value={fNumber(data?.amount_per_kg ?? 0, { disabledDecimal: true })}
          unit={t(species === SpeciesName.Fish ? 'transfer.gram' : 'transfer.amountPerKg')}
        />
        <InfoRow
          icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
          label={isFish ? t('transfer.cost.per.one') : t('transfer.cost.per.kg')}
          value={fNumber(data?.cost_per_kg ?? 0)}
          unit={t(isFish ? 'transfer.bahtPerPiece' : 'transfer.bahtPerKg')}
        />
        {estimate && (
          <InfoRow
            icon={<MonetizationOnOutlinedIcon fontSize="small" />}
            label={t('transfer.amountValue')}
            value={t('transfer.estimateValueRow', {
              amount: fInteger(estimate.amount),
              value: fInteger(estimate.value),
            })}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button variant="outlined" color="primary" onClick={onClose} sx={{ fontWeight: 600 }}>
          {t('transfer.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ShrimpTransferViewDialog
